import { forwardRef, ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Trans, useTranslation } from "react-i18next";

import { SvAnchor } from "@/components/SvAnchor/SvAnchor.tsx";
import { I18nKeyType } from "@/i18n";
import { cn } from "@/utils/tailwind";

const textVariants = cva("", {
  variants: {
    variant: {
      title: "scroll-m-20 text-2xl font-extrabold tracking-tight",
      subtitle: "scroll-m-20 text-3xl font-bold tracking-tight",
      header:
        "scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight first:mt-0",
      subheaderOne: "scroll-m-20 text-2xl font-semibold tracking-tight",
      subheaderTwo: "scroll-m-20 text-xl font-semibold tracking-tight",
      body: "leading-7",
      inline: "inline",
    },
  },
  defaultVariants: {
    variant: "body",
  },
});

export type TextVariantsType = NonNullable<
  VariantProps<typeof textVariants>["variant"]
>;

export type TextPropsType = {
  children?: ReactNode | ReactNode[];
  i18nKey: I18nKeyType;
  i18nOptions?: object;
  i18nValues?: Record<string, string>;
} & React.HTMLAttributes<HTMLHeadingElement> &
  VariantProps<typeof textVariants>;

const parseComponent = (variant: TextVariantsType) => {
  switch (variant) {
    case "title":
      return "h1";
    case "subtitle":
      return "h2";
    case "header":
      return "h3";
    case "subheaderOne":
      return "h4";
    case "subheaderTwo":
      return "h5";
    case "inline":
      return "span";
    default:
      return "p";
  }
};

export const SvText = forwardRef<HTMLHeadingElement, TextPropsType>(
  (
    {
      className,
      variant,
      i18nKey,
      i18nOptions,
      i18nValues,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = parseComponent(variant ?? "body");
    const { t } = useTranslation();

    return (
      <Comp
        className={cn(textVariants({ variant }), className)}
        ref={ref}
        {...props}>
        {i18nKey && (
          <Trans
            i18nOptions={i18nOptions}
            components={{
              b: <strong />,
              i: <em />,
              hl: <span className="highlight-neon-yellow text-dark" />,
              yellow: <span className="text-neon-yellow" />,
              bu: <span className="sketch-underline-primary" />,
              br: <br />,
              primary: <span className="text-primary" />,
              anchor: <SvAnchor href={i18nValues?.link ?? ""} />,
            }}>
            {t(i18nKey, i18nValues)}
          </Trans>
        )}
        {children}
      </Comp>
    );
  },
);
SvText.displayName = "Text";
